import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { AlertTriangle, FileText, ExternalLink, X } from 'lucide-react';
import MensagemAvisoMarkdown from '../../src/components/MensagemAvisoMarkdown';

// Modal de confirmação de um AvisoConsumidor, renderizado pelo PortalConsumidorGate.
// Reaproveita o esqueleto de PagamentoModal.tsx (bottom-sheet no mobile, modal
// centralizado no desktop), mas com duas diferenças deliberadas quando
// aviso.bloqueante === true: sem `onFechar` (sem X, sem clique-fora) e Esc é no-op —
// um aviso bloqueante não pode ser contornado. Toda confirmação (bloqueante ou não)
// exige checkbox + ter rolado até o fim do texto antes de habilitar o botão.

export interface AvisoModalAviso {
    id: string;
    titulo: string;
    mensagem: string;
    categoria: string;
    bloqueante: boolean;
    url_anexo?: string | null;
}

interface AvisoModalProps {
    aviso: AvisoModalAviso;
    posicao: number;
    total: number;
    onVisualizado: () => void;
    onConfirmar: () => Promise<void> | void;
    /** Presente só quando o aviso NÃO é bloqueante — habilita X e clique-fora. */
    onFechar?: () => void;
}

interface CategoriaConfig { label: string; color: string }

const CATEGORIAS: Record<string, CategoriaConfig> = {
    contrato: { label: 'Contrato', color: 'bg-secondary/15 text-secondary-dark' },
    aceite: { label: 'Aceite necessário', color: 'bg-secondary/15 text-secondary-dark' },
    novidade: { label: 'Novidade', color: 'bg-primary/10 text-primary' },
};
const CATEGORIA_PADRAO: CategoriaConfig = { label: 'Aviso', color: 'bg-gray-100 text-gray-600' };

const EXTENSOES_IMAGEM = ['jpg', 'jpeg', 'png', 'gif', 'webp'];

/** Extensão do arquivo (sem querystring/hash), em minúsculo — '' se não achar. */
const extensaoAnexo = (url: string): string => {
    const semQuery = url.split('?')[0].split('#')[0];
    const match = semQuery.match(/\.([a-zA-Z0-9]+)$/);
    return match ? match[1].toLowerCase() : '';
};

const AvisoModal = ({ aviso, posicao, total, onVisualizado, onConfirmar, onFechar }: AvisoModalProps) => {
    const [lido, setLido] = useState(false);
    const [aceite, setAceite] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [anexoAberto, setAnexoAberto] = useState(false);
    const jaMarcouVisto = useRef(false);
    const corpoRef = useRef<HTMLDivElement>(null);
    const dialogRef = useRef<HTMLDivElement>(null);

    const categoriaCfg = CATEGORIAS[aviso.categoria] || CATEGORIA_PADRAO;

    // PDF/imagem entram embutidos no corpo rolável do aviso — "rolar até o fim" já
    // obriga a passar pelo anexo, então nenhum estado extra é necessário pra saber
    // que foi visto. Outros formatos (doc/docx etc.) não têm preview confiável no
    // navegador: exigem abrir numa aba antes de liberar o aceite (anexoAberto).
    const extensao = aviso.url_anexo ? extensaoAnexo(aviso.url_anexo) : '';
    const anexoEhPdf = extensao === 'pdf';
    const anexoEhImagem = EXTENSOES_IMAGEM.includes(extensao);
    const anexoEmbutivel = anexoEhPdf || anexoEhImagem;
    const anexoPendente = !!aviso.url_anexo && !anexoEmbutivel && !anexoAberto;

    useEffect(() => {
        if (!jaMarcouVisto.current) {
            jaMarcouVisto.current = true;
            onVisualizado();
        }
    }, [onVisualizado]);

    // Foco entra no dialog assim que ele monta — importante para leitor de tela e
    // navegação por teclado começarem dentro do modal, não atrás dele.
    useEffect(() => {
        dialogRef.current?.focus();
    }, []);

    const aoRolar = () => {
        const el = corpoRef.current;
        if (!el || lido) return;
        const chegouAoFim = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
        if (chegouAoFim) setLido(true);
    };

    const confirmar = async () => {
        setEnviando(true);
        try {
            await onConfirmar();
        } finally {
            setEnviando(false);
        }
    };

    // Trap de foco real (não só visual): Tab/Shift+Tab ciclam só dentro do dialog.
    // Sem isso, um usuário de teclado consegue tabular "por trás" do modal e ativar
    // Sair/Meu Perfil do header mesmo com bloqueante=true — furando a garantia de
    // que o consumidor não navega sem confirmar. Esc é no-op quando não há onFechar
    // (aviso bloqueante), pelo mesmo motivo.
    const aoTeclar = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            if (!onFechar) e.preventDefault();
            return;
        }
        if (e.key !== 'Tab') return;
        // :not([disabled]) importa aqui: o checkbox e o botão "Li e aceito" ficam
        // disabled até o usuário rolar o texto e marcar a caixa. Um elemento disabled
        // nunca vira document.activeElement, então se ele sobrar como "primeiro" ou
        // "último" da lista, a comparação abaixo nunca bate e o Tab escapa do modal
        // (foi exatamente o caso encontrado: bloqueante=true + ainda não rolou até o
        // fim = checkbox e botão disabled = nenhum dos dois nunca é o activeElement).
        const focaveis = dialogRef.current?.querySelectorAll<HTMLElement>(
            'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
        );
        if (!focaveis || focaveis.length === 0) return;
        const primeiro = focaveis[0];
        const ultimo = focaveis[focaveis.length - 1];
        if (e.shiftKey && document.activeElement === primeiro) {
            e.preventDefault();
            ultimo.focus();
        } else if (!e.shiftKey && document.activeElement === ultimo) {
            e.preventDefault();
            primeiro.focus();
        }
    };

    return ReactDOM.createPortal(
        <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4 animate-fade-in"
            onClick={onFechar}
        >
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="aviso-modal-titulo"
                tabIndex={-1}
                onKeyDown={aoTeclar}
                className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-6xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col outline-none"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-3 pt-6 pb-4">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            {total > 1 && (
                                <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">
                                    Aviso {posicao} de {total}
                                </p>
                            )}
                            <span className={`inline-flex text-xs font-bold px-2.5 py-1 rounded-full ${categoriaCfg.color}`}>
                                {categoriaCfg.label}
                            </span>
                        </div>
                        {onFechar && (
                            <button onClick={onFechar} className="p-2 -mr-2 -mt-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors shrink-0">
                                <X size={20} />
                            </button>
                        )}
                    </div>
                    <h2 id="aviso-modal-titulo" className="text-xl font-bold text-gray-900 mt-3">{aviso.titulo}</h2>
                </div>

                <div
                    ref={corpoRef}
                    onScroll={aoRolar}
                    tabIndex={0}
                    aria-label="Conteúdo do aviso — role até o fim, incluindo o anexo quando houver, para habilitar a confirmação"
                    className="px-3 pb-4 overflow-y-auto flex-1 text-sm text-gray-700 leading-relaxed focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded"
                >
                    {/* A mensagem aceita formatação simples (negrito, itálico, listas) escrita
                        no painel de Config. `whitespace-pre-line` saiu do container de propósito:
                        as quebras de linha agora viram <br>/<p> no próprio markdown (remarkBreaks),
                        e manter os dois somava espaçamento em cima do texto já formatado. */}
                    <MensagemAvisoMarkdown value={aviso.mensagem} variante="portal" />

                    {aviso.url_anexo && anexoEmbutivel && (
                        <div className="mt-4">
                            {anexoEhPdf ? (
                                // #view=FitH: página estica pra largura do visualizador em vez de
                                // sobrar fundo escuro dos dois lados. #toolbar=0: some com a barra
                                // nativa (baixar, imprimir, anotar, girar) — a ideia aqui é só
                                // visualização do anexo dentro do fluxo do aviso, não um editor de PDF.
                                <iframe
                                    src={`${aviso.url_anexo}#view=FitH&toolbar=0`}
                                    title="Documento anexo"
                                    className="w-full h-[420px] sm:h-[560px] rounded-xl border border-gray-200"
                                />
                            ) : (
                                <img
                                    src={aviso.url_anexo}
                                    alt="Documento anexo"
                                    className="w-full rounded-xl border border-gray-200 object-contain"
                                />
                            )}
                            <a
                                href={aviso.url_anexo}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80"
                            >
                                Abrir em nova aba
                                <ExternalLink size={12} />
                            </a>
                        </div>
                    )}

                    {aviso.url_anexo && !anexoEmbutivel && (
                        <a
                            href={aviso.url_anexo}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setAnexoAberto(true)}
                            className={`mt-4 flex items-center gap-2 text-sm font-semibold ${anexoAberto ? 'text-gray-400' : 'text-primary hover:text-primary/80'}`}
                        >
                            <FileText size={16} />
                            {anexoAberto ? 'Documento anexo aberto' : 'Abrir documento anexo (obrigatório antes de continuar)'}
                            <ExternalLink size={13} />
                        </a>
                    )}
                </div>

                <div className="px-3 py-5 border-t border-gray-100 space-y-4">
                    <label className={`flex items-start gap-3 text-sm ${lido && !anexoPendente ? 'text-gray-700' : 'text-gray-400'}`}>
                        <input
                            type="checkbox"
                            checked={aceite}
                            disabled={!lido || anexoPendente}
                            onChange={(e) => setAceite(e.target.checked)}
                            className="mt-0.5 w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary/30 disabled:opacity-50"
                        />
                        <span>
                            Li e concordo com o comunicado acima.
                            {!lido && <span className="block text-xs mt-0.5">Role até o fim do texto para habilitar.</span>}
                            {lido && anexoPendente && <span className="block text-xs mt-0.5">Abra o documento anexo acima para habilitar.</span>}
                        </span>
                    </label>
                    {/* aria-live: o habilitar do checkbox é disparado por um evento de scroll (ou
                        clique no anexo), que leitor de tela nenhum anuncia sozinho — sem isso, quem
                        navega por teclado/leitor não fica sabendo que a caixa liberou. Região
                        visualmente oculta (sr-only) para não duplicar o texto já visível para quem
                        enxerga. */}
                    <span aria-live="polite" className="sr-only">
                        {lido && !anexoPendente && 'Aviso e anexo vistos até o fim. A caixa de confirmação está liberada.'}
                    </span>

                    <button
                        onClick={confirmar}
                        disabled={!lido || anexoPendente || !aceite || enviando}
                        className="w-full flex justify-center items-center gap-2 py-3.5 px-4 rounded-full text-sm font-bold text-white bg-primary hover:bg-primary/90 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all"
                    >
                        {enviando ? 'Confirmando...' : 'Li e aceito'}
                    </button>

                    {onFechar && (
                        <button
                            onClick={onFechar}
                            className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <AlertTriangle size={12} />
                            Ver depois
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default AvisoModal;
